/**
 * Social Media Hooks - Direct Database Implementation
 * 
 * These hooks provide direct access to the social media tables in the shared Supabase database.
 * They replace the BGV API integration with direct database queries.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useToast } from '@/components/ui/use-toast'
import { POST_STATUS, type PostStatus } from '@/constants/socialPostStatus'
import { useAuth } from '@/hooks/use-auth'
import type { RealtimeChannel } from '@supabase/supabase-js'

// Types
export interface SocialPost {
  id: string
  caption: string | null
  content: string | null
  image_urls: string[]
  status: PostStatus | 'QUEUED' // Keep QUEUED for backward compatibility
  scheduled_time: string | null
  published_at: string | null
  created_at: string
  updated_at: string
  profile_id: string | null
  queue_id: string | null
  queue_position: number | null
  platform_specific_data: any
  processing_logs: any[]
  error_message: string | null
  hashtags: string[]
  mentions: string[]
  tags: string[]
}

export interface SocialProfile {
  id: string
  name: string
  platform: 'INSTAGRAM' | 'FACEBOOK' | 'TWITTER' | 'LINKEDIN'
  account_id: string | null
  franchise_region: 'HQ' | 'north_york' | 'london' | 'toronto' | 'hamilton' | 'mississauga' | 'brampton' | 'scarborough' | 'general'
  credentials: any
  settings: any
  created_at: string
  updated_at: string
}

export interface Queue {
  id: string
  name: string
  description: string | null
  profile_id: string | null
  sort_order: 'date_asc' | 'date_desc' | 'custom'
  created_at: string
  updated_at: string
}

export interface PostFilters {
  status?: string[]
  profiles?: string[]
  date_range?: { start: string; end: string }
  search?: string
}

// Utility function to normalize post data
function normalizePost(post: any): SocialPost {
  return {
    ...post,
    image_urls: post.image_urls || [],
    hashtags: post.hashtags || [],
    mentions: post.mentions || [],
    tags: post.tags || [],
    processing_logs: post.processing_logs || []
  }
}

// Posts Hooks
export function usePosts(filters?: PostFilters) {
  return useQuery({
    queryKey: ['social-posts', filters],
    queryFn: async () => {
      let query = supabase
        .from('social_posts')
        .select('*')
        .order('created_at', { ascending: false })

      if (filters?.status && filters.status.length > 0) {
        query = query.in('status', filters.status)
      }

      if (filters?.profiles && filters.profiles.length > 0) {
        query = query.in('profile_id', filters.profiles)
      }

      if (filters?.date_range) {
        query = query
          .gte('created_at', filters.date_range.start)
          .lte('created_at', filters.date_range.end)
      }

      if (filters?.search) {
        query = query.or(`caption.ilike.%${filters.search}%,content.ilike.%${filters.search}%`)
      }

      const { data, error } = await query

      if (error) throw error
      
      // Normalize array fields to ensure they're never null
      return (data || []).map(normalizePost)
    }
  })
}

export function usePost(id: string) {
  return useQuery({
    queryKey: ['social-post', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_posts')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      
      // Normalize array fields to ensure they're never null
      return normalizePost(data)
    },
    enabled: !!id
  })
}

export function useCreatePost() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (post: Partial<SocialPost>) => {
      console.log('useCreatePost: Raw post data received:', post)
      
      // Server-side validation
      if (!post.caption || post.caption.trim().length < 10) {
        throw new Error('Caption must be at least 10 characters long')
      }
      
      if (!post.profile_id || post.profile_id.trim() === '') {
        throw new Error('Please select a social media profile')
      }
      
      // Validate scheduled time if provided
      if (post.scheduled_time) {
        const scheduledDate = new Date(post.scheduled_time)
        if (scheduledDate <= new Date()) {
          throw new Error('Scheduled time must be in the future')
        }
      }
      
      // Clean up UUID fields - convert empty strings to null
      // Also exclude the id field to let the database generate it
      const { id, created_at, updated_at, ...postWithoutSystemFields } = post
      
      // Determine status based on scheduled_time
      const status = post.scheduled_time ? POST_STATUS.SCHEDULED : POST_STATUS.DRAFT
      
      const cleanedPost = {
        ...postWithoutSystemFields,
        profile_id: post.profile_id || null,
        queue_id: post.queue_id || null,
        status: post.status || status, // Use determined status if not provided
        // Ensure arrays are not null
        hashtags: post.hashtags || [],
        image_urls: post.image_urls || [],
        mentions: post.mentions || [],
        tags: post.tags || []
        // Let database handle created_at and updated_at with defaults
      }

      console.log('useCreatePost: Cleaned post data for database:', cleanedPost)

      const { data, error } = await supabase
        .from('social_posts')
        .insert(cleanedPost)
        .select()
        .single()

      console.log('useCreatePost: Database response:', { data, error })

      if (error) throw error
      return data as SocialPost
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      toast({
        title: "✅ Post created successfully!",
        description: "Your post has been saved and is ready to publish.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to create post",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
        duration: 0, // Don't auto-dismiss errors
      })
    }
  })
}

export function useUpdatePost() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SocialPost> & { id: string }) => {
      // Show loading toast
      const loadingToast = toast({
        title: "Updating post...",
        description: "Please wait while we save your changes",
        duration: 100000, // Keep it visible
      })
      // Server-side validation
      if (updates.caption !== undefined && (!updates.caption || updates.caption.trim().length < 10)) {
        throw new Error('Caption must be at least 10 characters long')
      }
      
      if (updates.profile_id !== undefined && (!updates.profile_id || updates.profile_id.trim() === '')) {
        throw new Error('Please select a social media profile')
      }
      
      // Validate scheduled time if provided
      if (updates.scheduled_time) {
        const scheduledDate = new Date(updates.scheduled_time)
        if (scheduledDate <= new Date()) {
          throw new Error('Scheduled time must be in the future')
        }
      }
      
      // Clean up UUID fields - convert empty strings to null
      const cleanedUpdates = {
        ...updates,
        profile_id: updates.profile_id || null,
        queue_id: updates.queue_id || null,
        updated_at: new Date().toISOString(),
        // Ensure arrays are not null
        hashtags: updates.hashtags || [],
        image_urls: updates.image_urls || [],
        mentions: updates.mentions || [],
        tags: updates.tags || []
      }

      const { data, error } = await supabase
        .from('social_posts')
        .update(cleanedUpdates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as SocialPost
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      toast({
        title: "✅ Post updated successfully!",
        description: "Your changes have been saved.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to update post",
        description: error.message || "Unable to save changes. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

export function useDeletePost() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('social_posts')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      toast({
        title: "✅ Post deleted",
        description: "The post has been removed.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to delete post",
        description: error.message || "Unable to delete the post. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

// Profiles Hooks
export function useProfiles() {
  return useQuery({
    queryKey: ['social-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_profiles')
        .select('*')
        .order('name')

      if (error) throw error
      return data as SocialProfile[]
    }
  })
}

export function useCreateProfile() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (profile: Partial<SocialProfile>) => {
      const { data, error } = await supabase
        .from('social_profiles')
        .insert({
          ...profile,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single()

      if (error) throw error
      return data as SocialProfile
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
      toast({
        title: "✅ Profile created",
        description: "Social media profile has been added successfully.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to create profile",
        description: error.message || "Unable to create profile. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<SocialProfile> & { id: string }) => {
      const { data, error } = await supabase
        .from('social_profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as SocialProfile
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
      toast({
        title: "✅ Profile updated",
        description: "Changes saved successfully.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to update profile",
        description: error.message || "Unable to save changes. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

export function useDeleteProfile() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('social_profiles')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-profiles'] })
      toast({
        title: "✅ Profile deleted",
        description: "The profile has been removed.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to delete profile",
        description: error.message || "Unable to delete profile. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

// Queues Hooks
export function useQueues(profileId?: string) {
  return useQuery({
    queryKey: ['social-queues', profileId],
    queryFn: async () => {
      let query = supabase
        .from('queues')
        .select('*')
        .order('name')

      if (profileId) {
        query = query.eq('profile_id', profileId)
      }

      const { data, error } = await query

      if (error) throw error
      return data as Queue[]
    }
  })
}

export function useCreateQueue() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (queue: Partial<Queue>) => {
      const { data, error } = await supabase
        .from('queues')
        .insert({
          ...queue,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single()

      if (error) throw error
      return data as Queue
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-queues'] })
      toast({
        title: "✅ Queue created",
        description: "Your new queue is ready to use.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to create queue",
        description: error.message || "Unable to create queue. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

export function useUpdateQueue() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Queue> & { id: string }) => {
      const { data, error } = await supabase
        .from('queues')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Queue
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-queues'] })
      toast({
        title: "✅ Queue updated",
        description: "Changes saved successfully.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to update queue",
        description: error.message || "Unable to save changes. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

export function useDeleteQueue() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('queues')
        .delete()
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['social-queues'] })
      toast({
        title: "✅ Queue deleted",
        description: "The queue has been removed.",
        duration: 3000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Failed to delete queue",
        description: error.message || "Unable to delete queue. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

// Edge Function Hooks
export function usePublishPost() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({ postId, testMode = true }: { postId: string; testMode?: boolean }) => {
      // Show loading toast
      const loadingToast = toast({
        title: "📤 Publishing post...",
        description: testMode ? "Running in test mode" : "Publishing to Instagram",
        duration: 100000,
      })
      
      const { data, error } = await supabase.functions.invoke('create-instagram-post', {
        body: { postId, testMode }
      })

      if (error) throw error
      return data
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      toast({
        title: "✅ Post published!",
        description: variables.testMode 
          ? "Test publish completed successfully" 
          : "Your post is now live on Instagram",
        duration: 5000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Publishing failed",
        description: error.message || "Unable to publish post. Please check your connection and try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

export function useProcessScheduledPosts() {
  const queryClient = useQueryClient()
  const { toast, dismiss } = useToast()

  return useMutation({
    mutationFn: async () => {
      // Show loading toast
      const loadingToast = toast({
        title: "🔄 Processing scheduled posts...",
        description: "Checking for posts ready to publish",
        duration: 0, // Don't auto-dismiss
      })
      
      try {
        const { data, error } = await supabase.functions.invoke('process-scheduled-posts')
        
        // Dismiss loading toast
        dismiss(loadingToast.id)
        
        if (error) throw error
        return data
      } catch (error) {
        // Dismiss loading toast on error
        dismiss(loadingToast.id)
        throw error
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      const processedCount = data.processed || 0
      toast({
        title: processedCount > 0 ? "✅ Posts processed!" : "ℹ️ No posts to process",
        description: processedCount > 0 
          ? `Successfully processed ${processedCount} scheduled ${processedCount === 1 ? 'post' : 'posts'}.`
          : "No scheduled posts are ready for publishing at this time.",
        duration: 4000,
      })
    },
    onError: (error: any) => {
      toast({
        title: "❌ Processing failed",
        description: error.message || "Unable to process scheduled posts. Please try again.",
        variant: "destructive",
        duration: 0,
      })
    }
  })
}

// Utility Hooks
export function useScheduledPosts() {
  return useQuery({
    queryKey: ['scheduled-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('social_posts')
        .select('*')
        .in('status', ['SCHEDULED', 'QUEUED'])
        .lte('scheduled_time', new Date().toISOString())
        .order('scheduled_time')

      if (error) throw error
      
      // Normalize array fields to ensure they're never null
      return (data || []).map(normalizePost)
    }
  })
}

// Gallery Integration
export function useApprovedGalleries() {
  return useQuery({
    queryKey: ['approved-galleries'],
    queryFn: async () => {
      try {
        // First, get approved galleries
        const { data: galleries, error } = await supabase
          .from('galleries')
          .select('id, title, description, franchise_region, review_status, is_public, created_on, cover_image')
          .eq('review_status', 'approved')
          .eq('is_public', true)
          .order('created_on', { ascending: false })

        if (error) {
          console.error('Error fetching galleries:', error)
          throw error
        }

        if (!galleries || galleries.length === 0) {
          console.log('No approved galleries found')
          return []
        }

        // Import GalleryDataAccess dynamically to avoid circular dependencies
        const { GalleryDataAccess } = await import('@/lib/galleryDataAccess')

        // For each gallery, get images using the unified data access layer
        const galleriesWithPhotos = await Promise.all(
          galleries.map(async (gallery) => {
            try {
              // Use the legacy compatibility method to get images in the expected format
              const galleryPhotos = await GalleryDataAccess.getGalleryPhotos(gallery.id)
              
              return {
                ...gallery,
                gallery_photos: galleryPhotos
              }
            } catch (imageError) {
              console.warn(`Failed to fetch images for gallery ${gallery.id}:`, imageError)
              // Return gallery with empty photos array as fallback
              return {
                ...gallery,
                gallery_photos: []
              }
            }
          })
        )

        console.log(`✅ Successfully fetched ${galleriesWithPhotos.length} approved galleries with unified image data`)
        return galleriesWithPhotos

      } catch (error) {
        console.error('Error in useApprovedGalleries:', error)
        throw error
      }
    },
    // Add error retry configuration
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for permission/auth errors
      if (failureCount >= 2) return false
      if (error?.message?.includes('JWT') || error?.message?.includes('permission')) return false
      return true
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Cache for 5 minutes since gallery data doesn't change frequently
    staleTime: 5 * 60 * 1000,
    // Keep in cache for 10 minutes when not in use
    gcTime: 10 * 60 * 1000
  })
}

// Real-time Status Updates Hook
export function usePostStatusSubscription() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user } = useAuth()
  const channelRef = useRef<RealtimeChannel | null>(null)
  const processingSoundRef = useRef<HTMLAudioElement | null>(null)
  const successSoundRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (!user?.id) return

    // Initialize notification sounds (optional - can be toggled via settings)
    try {
      processingSoundRef.current = new Audio('/sounds/processing.mp3')
      successSoundRef.current = new Audio('/sounds/success.mp3')
    } catch (e) {
      // Sounds are optional, continue without them
      console.debug('Notification sounds not available')
    }

    // Create channel name specific to user
    const channelName = `social-posts-updates:${user.id}`
    
    // Subscribe to post status changes
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'social_posts',
          // Note: We can't filter by user_id in the subscription itself since social_posts doesn't have user_id
          // We'll filter client-side based on profile ownership
        },
        async (payload) => {
          const newPost = payload.new as SocialPost
          const oldPost = payload.old as SocialPost
          
          // Skip if status hasn't changed
          if (newPost.status === oldPost.status) return
          
          // Fetch the profile to check ownership
          const { data: profile } = await supabase
            .from('social_profiles')
            .select('*')
            .eq('id', newPost.profile_id)
            .single()
          
          // Skip if profile doesn't belong to current user (basic check - enhance as needed)
          // This is a simplified check - you may need to enhance based on your auth model
          if (!profile) return

          // Update React Query cache for all post-related queries
          // Update main posts list
          queryClient.setQueriesData(
            { queryKey: ['social-posts'], exact: false },
            (oldData: SocialPost[] | undefined) => {
              if (!oldData) return oldData
              return oldData.map(post => 
                post.id === newPost.id ? normalizePost(newPost) : post
              )
            }
          )

          // Update individual post query if it exists
          queryClient.setQueryData(['social-post', newPost.id], normalizePost(newPost))

          // Invalidate scheduled posts if status changed to/from SCHEDULED
          if (oldPost.status === POST_STATUS.SCHEDULED || newPost.status === POST_STATUS.SCHEDULED) {
            queryClient.invalidateQueries({ queryKey: ['scheduled-posts'] })
          }

          // Show toast notifications based on status change
          switch (newPost.status) {
            case POST_STATUS.PROCESSING:
              // Play processing sound
              processingSoundRef.current?.play().catch(() => {})
              
              toast({
                title: "📤 Publishing post...",
                description: `Your post is being published to ${profile.platform}`,
                duration: 0, // Keep visible until status changes
                // Store toast ID so we can dismiss it later
                onOpenChange: (open) => {
                  if (!open) {
                    // User dismissed, store preference if needed
                  }
                }
              })
              break

            case POST_STATUS.POSTED:
              // Play success sound
              successSoundRef.current?.play().catch(() => {})
              
              // Dismiss any processing toasts
              toast({
                title: "✅ Post published successfully!",
                description: newPost.platform_specific_data?.instagram_post_id 
                  ? `Your post is now live on ${profile.platform}. Click to view: https://www.instagram.com/p/${newPost.platform_specific_data.instagram_post_id}/`
                  : `Your post is now live on ${profile.platform}`,
                duration: 5000
              })
              break

            case POST_STATUS.ERROR:
              toast({
                title: "❌ Publishing failed",
                description: newPost.error_message || "Failed to publish post. Please try again.",
                variant: "destructive",
                duration: 0 // Keep visible until user dismisses
              })
              break

            case POST_STATUS.SCHEDULED:
              // Post was rescheduled
              if (oldPost.status === POST_STATUS.ERROR || oldPost.status === POST_STATUS.PROCESSING) {
                toast({
                  title: "🕐 Post rescheduled",
                  description: `Post scheduled for ${new Date(newPost.scheduled_time!).toLocaleString()}`,
                  duration: 3000
                })
              }
              break
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.debug('Subscribed to post status updates')
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Failed to subscribe to post status updates')
          
          // Attempt to reconnect after a delay
          setTimeout(() => {
            if (channelRef.current) {
              channelRef.current.subscribe()
            }
          }, 5000)
        }
      })

    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [user?.id, queryClient, toast])

  // Handle visibility change to check for updates when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && channelRef.current?.state === 'joined') {
        // Check for updates when returning to the tab
        queryClient.invalidateQueries({ queryKey: ['social-posts'] })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [queryClient])

  // Function to manually check for stale data after reconnection
  const checkForUpdates = async () => {
    await queryClient.invalidateQueries({ queryKey: ['social-posts'] })
  }

  return {
    isConnected: channelRef.current?.state === 'joined',
    checkForUpdates
  }
}